// src/services/scannerSession.js
//
// A phone logged in as a scanner keeps a long-lived device token in
// localStorage (like orbit_perm_infoscreen) and trades it for short-lived
// access tokens. The scanner's access token lives in memory only, never in
// `access_token`, so AuthContext never mistakes the scanner for a staff login.
import axios from 'axios';
import i18n from 'i18next';

export const SCANNER_STORAGE_KEY = 'orbit_perm_scanner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

let accessToken = null;
let pendingResume = null;

export function getStoredScanner() {
  try {
    const raw = localStorage.getItem(SCANNER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isScannerDevice() {
  return !!getStoredScanner();
}

export function getScannerAccessToken() {
  return accessToken;
}

// Store a login/session response and return it
export function applyScannerSession(session) {
  localStorage.setItem(SCANNER_STORAGE_KEY, JSON.stringify({
    device_token: session.device_token,
    scanner: session.scanner,
    department_name: session.department_name,
  }));
  accessToken = session.access_token || null;
  return session;
}

export async function scannerLogin({ department_name, name, password }) {
  const response = await axios.post(`${API_BASE_URL}/api/v1/inventory/scanners/login`, {
    department_name, name, password,
  });
  return applyScannerSession(response.data.data);
}

// Exchange the stored device token for a fresh session. Concurrent callers
// share one request. Throws with `expired = true` when the scanner was
// deleted or its password changed; the device is then logged out.
export function resumeScannerSession() {
  if (pendingResume) return pendingResume;
  const stored = getStoredScanner();
  if (!stored?.device_token) return Promise.reject(Object.assign(new Error('No scanner session'), { expired: true }));

  pendingResume = axios
    .post(`${API_BASE_URL}/api/v1/inventory/scanners/session`, { device_token: stored.device_token })
    .then((response) => applyScannerSession(response.data.data))
    .catch((error) => {
      if (error.response?.status === 401) {
        clearScannerSession();
        error.expired = true;
      }
      throw error;
    })
    .finally(() => { pendingResume = null; });
  return pendingResume;
}

/**
 * Staff PIN (or password) login on a scanner that requires it. Stores a normal
 * staff token pair, which also carries the scanner's scan permissions.
 * Throws an Error with a translated message.
 */
export async function scannerStaffLogin({ staff_member_name, pin, password }) {
  const stored = getStoredScanner();
  try {
    const response = await axios.post(`${API_BASE_URL}/api/v1/inventory/scanners/staff-login`, {
      device_token: stored?.device_token,
      staff_member_name,
      pin: pin ?? null,
      password: password ?? null,
    });
    const { token, refresh_token } = response.data.data;
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refresh_token);
    return { token };
  } catch (error) {
    const data = error.response?.data;
    const code = data?.error_code;
    const message = code && i18n.exists(`errors:${code}`)
      ? i18n.t(`errors:${code}`)
      : (data?.message || i18n.t('errors:NETWORK'));
    throw Object.assign(new Error(message), { code });
  }
}

export function clearScannerSession() {
  localStorage.removeItem(SCANNER_STORAGE_KEY);
  accessToken = null;
}
